import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { getMe, logout, getClassrooms, createClassroom, getObjectiveStats } from '../lib/api';
import { toast } from 'sonner';
import { 
  Keyboard, Plus, Users, BookOpen, LogOut, 
  BarChart3, Copy, ChevronRight, Target, Shield, Brain, FolderOpen
} from 'lucide-react';

const GRADE_LEVELS = ['K', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];

const TeacherDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [classrooms, setClassrooms] = useState([]);
  const [objectiveStats, setObjectiveStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newClass, setNewClass] = useState({ name: '', grade_level: '' });

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!user) {
          const response = await getMe();
          if (response.data.role !== 'teacher') {
            navigate('/student');
            return;
          }
          setUser(response.data);
        }
        const [classroomsRes, statsRes] = await Promise.all([
          getClassrooms(),
          getObjectiveStats()
        ]);
        setClassrooms(classroomsRes.data);
        setObjectiveStats(statsRes.data);
      } catch {
        navigate('/');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user, navigate]);

  const handleCreateClass = async () => {
    if (!newClass.name || !newClass.grade_level) {
      toast.error('Please fill in all fields');
      return;
    }
    setCreating(true);
    try {
      const response = await createClassroom(newClass);
      setClassrooms([...classrooms, { ...response.data, student_count: 0 }]);
      toast.success('Classroom created!');
      setDialogOpen(false);
      setNewClass({ name: '', grade_level: '' });
    } catch (error) {
      toast.error('Failed to create classroom');
    } finally {
      setCreating(false);
    }
  };

  const copyClassCode = async (code) => {
    try {
      // Try modern clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(code);
      } else {
        // Fallback for environments where clipboard API is blocked
        const textArea = document.createElement('textarea');
        textArea.value = code;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      toast.success('Class code copied!');
    } catch (err) {
      // Fallback method
      const textArea = document.createElement('textarea');
      textArea.value = code;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        toast.success('Class code copied!');
      } catch {
        toast.error('Could not copy. Code: ' + code);
      }
      document.body.removeChild(textArea);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-white/5 bg-background-paper/50 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
              <Keyboard className="w-5 h-5 text-primary" />
            </div>
            <span className="font-heading text-xl font-bold">LitBlitz</span>
          </div>
          <div className="flex items-center gap-4">
            {user?.is_admin && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => navigate('/admin')}
                data-testid="admin-btn"
                className="border-primary/50 text-primary hover:bg-primary/10"
              >
                <Shield className="w-4 h-4 mr-2" />
                Admin
              </Button>
            )}
            <span className="text-text-secondary text-sm hidden sm:block">{user?.email}</span>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleLogout}
              data-testid="logout-btn"
              className="text-text-secondary hover:text-white"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Welcome */}
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold mb-2" data-testid="teacher-welcome">
            Welcome back, {user?.name?.split(' ')[0]}!
          </h1>
          <p className="text-text-secondary">Manage your classrooms and track student progress</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
          <Card className="card-glass p-6" data-testid="stat-classrooms">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center">
                <Users className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-text-secondary text-sm">Classrooms</p>
                <p className="font-heading text-2xl font-bold">{classrooms.length}</p>
              </div>
            </div>
          </Card>
          <Card className="card-glass p-6" data-testid="stat-students">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-secondary/20 flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-secondary" />
              </div>
              <div>
                <p className="text-text-secondary text-sm">Total Students</p>
                <p className="font-heading text-2xl font-bold">
                  {classrooms.reduce((acc, c) => acc + (c.student_count || 0), 0)}
                </p>
              </div>
            </div>
          </Card>
          <Card 
            className="card-glass p-6 cursor-pointer hover:border-status-warning/30 transition-colors" 
            data-testid="stat-objectives"
            onClick={() => navigate('/objectives')}
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-status-warning/20 flex items-center justify-center">
                <Target className="w-6 h-6 text-status-warning" />
              </div>
              <div>
                <p className="text-text-secondary text-sm">Objectives Tracked</p>
                <p className="font-heading text-2xl font-bold">{objectiveStats?.typing_by_objective?.length || objectiveStats?.quiz_by_objective?.length || 0}</p>
              </div>
            </div>
          </Card>
          <Card 
            className="card-glass p-6 cursor-pointer hover:border-neon-red/30 transition-colors" 
            data-testid="stat-analytics"
            onClick={() => navigate('/analytics')}
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-neon-red/20 flex items-center justify-center">
                <Brain className="w-6 h-6 text-neon-red" />
              </div>
              <div>
                <p className="text-text-secondary text-sm">Question Analytics</p>
                <p className="font-heading text-sm font-bold text-neon-red">View Insights →</p>
              </div>
            </div>
          </Card>
          <Card className="card-glass p-6" data-testid="stat-completions">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-status-success/20 flex items-center justify-center">
                <BarChart3 className="w-6 h-6 text-status-success" />
              </div>
              <div>
                <p className="text-text-secondary text-sm">Total Completions</p>
                <p className="font-heading text-2xl font-bold">{objectiveStats?.overview?.total_completions || 0}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Classrooms Section */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-heading text-xl font-bold">Your Classrooms</h2>
          <div className="flex items-center gap-3">
            <Button 
              variant="outline"
              className="border-secondary/50 text-secondary hover:bg-secondary/10 rounded-sm"
              onClick={() => navigate('/create-exercise?tab=template')}
              data-testid="library-btn"
            >
              <FolderOpen className="w-4 h-4 mr-2" />
              Library
            </Button>
            <Button 
              className="btn-secondary rounded-sm"
              onClick={() => navigate('/create-exercise')}
              data-testid="quick-create-exercise-btn"
            >
              <Keyboard className="w-4 h-4 mr-2" />
              Create Exercise
            </Button>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  className="btn-primary rounded-sm"
                  data-testid="create-classroom-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  New Classroom
                </Button>
              </DialogTrigger>
            <DialogContent className="bg-background-paper border-white/10">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">Create New Classroom</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <div>
                  <label className="text-sm text-text-secondary mb-2 block">Classroom Name</label>
                  <Input
                    className="input-cyber"
                    placeholder="e.g., 5th Grade ELA"
                    value={newClass.name}
                    onChange={(e) => setNewClass({ ...newClass, name: e.target.value })}
                    data-testid="classroom-name-input"
                  />
                </div>
                <div>
                  <label className="text-sm text-text-secondary mb-2 block">Grade Level</label>
                  <Select 
                    value={newClass.grade_level} 
                    onValueChange={(val) => setNewClass({ ...newClass, grade_level: val })}
                  >
                    <SelectTrigger className="input-cyber" data-testid="grade-level-select">
                      <SelectValue placeholder="Select grade" />
                    </SelectTrigger>
                    <SelectContent className="bg-background-paper border-white/10">
                      {GRADE_LEVELS.map(grade => (
                        <SelectItem key={grade} value={grade}>
                          Grade {grade}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button 
                  className="btn-primary w-full rounded-sm"
                  onClick={handleCreateClass}
                  disabled={creating}
                  data-testid="submit-classroom-btn"
                >
                  {creating ? 'Creating...' : 'Create Classroom'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Classroom Grid */}
        {classrooms.length === 0 ? (
          <Card className="card-glass p-12 text-center" data-testid="no-classrooms">
            <Users className="w-12 h-12 text-text-muted mx-auto mb-4" />
            <h3 className="font-heading text-lg font-bold mb-2">No classrooms yet</h3>
            <p className="text-text-secondary mb-6">Create your first classroom to get started</p>
            <Button 
              className="btn-primary rounded-sm"
              onClick={() => setDialogOpen(true)}
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Classroom
            </Button>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {classrooms.map((classroom) => (
              <Card 
                key={classroom.classroom_id}
                className="card-glass p-6 cursor-pointer group"
                onClick={() => navigate(`/classroom/${classroom.classroom_id}`)}
                data-testid={`classroom-card-${classroom.classroom_id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center">
                    <Users className="w-6 h-6 text-primary" />
                  </div>
                  <div 
                    className="flex items-center gap-1 px-3 py-1 rounded-full bg-white/5 cursor-pointer hover:bg-white/10 transition-colors"
                    onClick={(e) => {
                      e.stopPropagation();
                      copyClassCode(classroom.class_code);
                    }}
                    data-testid={`copy-code-${classroom.classroom_id}`}
                  >
                    <span className="text-sm font-mono text-primary">{classroom.class_code}</span>
                    <Copy className="w-3 h-3 text-primary" />
                  </div>
                </div>
                <h3 className="font-heading text-lg font-bold mb-1 group-hover:text-primary transition-colors">
                  {classroom.name}
                </h3>
                <p className="text-text-secondary text-sm mb-4">Grade {classroom.grade_level}</p>
                <div className="flex items-center justify-between">
                  <span className="text-text-muted text-sm">
                    {classroom.student_count || 0} students
                  </span>
                  <ChevronRight className="w-4 h-4 text-text-muted group-hover:text-primary transition-colors" />
                </div>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default TeacherDashboard;
